import React, {useCallback, useEffect, useMemo, useRef, useState, forwardRef} from 'react';
import { useUserContext } from "../../contexts";
import useHeaderMenu from './useHeaderMenu';

import './Menu.scss';

const LOCAL_DEV_HOSTNAMES = ['localhost', '127.0.0.1'];
const INTERNAL_MENU_HOSTS = new Set([
    'productioncrate.com',
    'www.productioncrate.com',
    'user.productioncrate.com',
    'user-qa.productioncrate.com',
    'staging.productioncrate.com',
    'app.productioncrate.com',
    'vfx.productioncrate.com',
    'graphics.productioncrate.com',
    'sfx.productioncrate.com',
    'rendercrate.com',
    'footagecrate.com',
    'graphicscrate.com',
    'soundscrate.com',
]);

const isLocalDevelopmentOrigin = (origin) => {
    try {
        const parsed = new URL(origin);
        return LOCAL_DEV_HOSTNAMES.includes(parsed.hostname) || parsed.hostname.endsWith('.localhost');
    } catch (error) {
        return false;
    }
};

const rewriteMenuUrlToCurrentOrigin = (url, currentOrigin) => {
    if (!url || !isLocalDevelopmentOrigin(currentOrigin)) {
        return url;
    }

    if (url.startsWith('mailto:')) {
        return url;
    }

    try {
        const parsed = new URL(url);
        if (!INTERNAL_MENU_HOSTS.has(parsed.hostname)) {
            return url;
        }
        return `${currentOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (error) {
        return url;
    }
};

const rewritePluginsMenuUrlToProductionCrate = (url) => {
    if (!url || url.startsWith('mailto:')) {
        return url;
    }

    try {
        const parsed = new URL(url);
        parsed.protocol = 'https:';
        parsed.hostname = 'www.productioncrate.com';
        return parsed.toString();
    } catch (error) {
        return url;
    }
};

export const buildMenuColumns = (categories, maxRows) => {
    if (!categories.length) {
        return [];
    }

    let remainingColumns = 1;
    let rows = 0;
    categories.forEach((category) => {
        const categoryRows = 1 + (category.subcategories?.length || 0);
        if (rows && (rows + categoryRows >= maxRows || category.jumpColumn)) {
            remainingColumns++;
            rows = 0;
        }
        rows += categoryRows;
    });

    const categoryHeight = (category) => (category.name ? 2 : 1) + (category.subcategories?.length || 0);
    let remainingHeight = categories.reduce((total, category) => total + categoryHeight(category), 0);
    let remainingJumps = categories.slice(1).filter(category => category.jumpColumn).length;
    const columns = [];
    let column = [];
    let columnHeight = 0;

    categories.forEach((category) => {
        const height = categoryHeight(category);
        const targetHeight = remainingHeight / remainingColumns;
        if (category.jumpColumn && column.length) {
            remainingJumps--;
        }
        if (column.length && (category.jumpColumn || (
            remainingColumns > remainingJumps + 1
            && Math.abs(targetHeight - columnHeight) <= Math.abs(targetHeight - columnHeight - height)
        ))) {
            columns.push(column);
            remainingHeight -= columnHeight;
            remainingColumns--;
            column = [];
            columnHeight = 0;
        }
        column.push(category);
        columnHeight += height;
    });
    columns.push(column);
    return columns;
};

const Menu = ({visible, closeCallback, onLogout, external}, ref) => {
    
    const {user} = useUserContext();
    const headerMenu = useHeaderMenu();
    const mainCategories = useMemo(
        () => Array.isArray(headerMenu?.mainCategories) ? headerMenu.mainCategories : [],
        [headerMenu]
    );

    const floatingMenuRef = useRef(null);
    
    const [floatingMenuExpand, setFloatingMenuExpand] = useState(null);

    const [currentPath, setCurrentPath] = useState(null);
    const [currentPathResolved, setCurrentPathResolved] = useState(false);
    
    //NEW MENU LOGIC
    const [menuLeft, setMenuLeft] = useState(0);
    const [menuWidth, setMenuWidth] = useState(0);

    const baseHref = (() => {
        if (!external) {
            return '';
        }

        const currentOrigin = window.location.origin || '';
        const hostname = window.location.hostname || '';

        if (isLocalDevelopmentOrigin(currentOrigin)) {
            return currentOrigin;
        }

        if (hostname.includes('user-qa') || hostname.includes('staging')) {
            return 'https://user-qa.productioncrate.com';
        }

        return 'https://user.productioncrate.com';
    })();

    const renderMainCategoryLabel = (mainCategory) => (
        <>
            {mainCategory.id === 'plugins' &&
            <img
                className="main-category-icon"
                src={`${baseHref}/images/icon.plugins.fire.svg`}
                alt=""
                aria-hidden="true"
            />
            }
            <span>{mainCategory.name}</span>
        </>
    );

    useEffect(() => {
        if(floatingMenuExpand === null || !visible || !floatingMenuRef || !floatingMenuRef.current){
            return;
        }
        //console.log(floatingMenuExpand)
        window.scrollTo(0, 0);
        floatingMenuRef.current.scrollTo({ top: 0, behavior: 'smooth' });

    }, [floatingMenuExpand]);

    const initCurrentPath = useCallback(() => {
        let path = window.location.pathname;
        if (path === '' || path === '/') {
            path = 'home'
        }

        if (!mainCategories.some(m => path.includes('/'+m.id))) {
            path = null;
        }
        setCurrentPath(path);
        setCurrentPathResolved(true);
    }, [mainCategories]);
      
    const replaceDomainsLoggedIn = (str, loggedIn, vertical, category, subcategory, customLink=false) => {
        const locationOrigin = window.location.origin || 'https://www.productioncrate.com';
        const linkSource = customLink || str;

        // Plugin menu links must always point to productioncrate.com, regardless of environment.
        if (vertical === 'plugins' || category === 'plugins') {
            return rewritePluginsMenuUrlToProductionCrate(linkSource);
        }

        if (vertical !== 'plugins' && category !== 'plugins' && vertical !== 'home') {
            let uriPath = locationOrigin;

            switch (vertical) {
                case '3d':
                    uriPath = 'https://rendercrate.com';
                    break;
                case 'graphics':
                    uriPath = 'https://www.productioncrate.com';
                    break;
                case 'vfx/motion-graphics':
                case 'vfx/visual-graphics':
                    uriPath = 'https://www.productioncrate.com';
                    break;
                case 'sfx':
                    uriPath = 'https://www.productioncrate.com';
                    break;
            }

            
            // this case becomes almost dummy as any logged-in user already is in productioncrate.com domain...
            if (loggedIn) {
                uriPath = 'https://www.productioncrate.com';
            }

            // Keep production vertical links on the canonical domain.
            if(locationOrigin.includes('productioncrate.com')){
                switch (vertical) {
                    case '3d':
                        uriPath = 'https://www.productioncrate.com';
                        break;
                    case 'graphics':
                        uriPath = 'https://www.productioncrate.com';
                        break;
                    case 'vfx/motion-graphics':
                    case 'vfx/visual-graphics':
                        uriPath = 'https://www.productioncrate.com';
                        break;
                    case 'sfx':
                        uriPath = 'https://www.productioncrate.com';
                        break;
                }
            }
            // testing environment... when we are on localhost, keep beein there//
            if (isLocalDevelopmentOrigin(locationOrigin)){
                uriPath = locationOrigin;
            }
            // on account-qa or on staging let's keep staging subdomain for links
            if (locationOrigin.includes('staging.') || locationOrigin.includes('user-qa.')) {
                uriPath = 'https://staging.productioncrate.com';
            }

            if(vertical && category && subcategory && category === 'search') {
                let searchVertical = vertical;
                if (vertical === 'vfx/motion-graphics' || vertical === 'vfx/visual-graphics') {
                    searchVertical = 'vfx';
                }
                uriPath += `/search/${subcategory}?main_category=${searchVertical}`;
                return uriPath.toLocaleLowerCase();
            }

            if (vertical) {
                vertical.replace('home', '');
                uriPath += `/${vertical}`;
            }
            if (category && category !== 'view-all') {
                uriPath += `/${category}`;
            }
    
            if (subcategory) {
                uriPath += `/${subcategory}`;
            }
            
            if (customLink) {
                return rewriteMenuUrlToCurrentOrigin(customLink, locationOrigin);
            }
            return rewriteMenuUrlToCurrentOrigin(uriPath.toLocaleLowerCase(), locationOrigin);
            //return mapOldUrlSchemeToNew(str);
        }

        let finalUrl = !loggedIn?str:str.replace('rendercrate.com', 'productioncrate.com')
                        .replace('graphicscrate.com', 'graphics.productioncrate.com')
                        .replace('footagecrate.com', 'vfx.productioncrate.com')
                        .replace('soundscrate.com', 'sfx.productioncrate.com');
        if (finalUrl === 'https://rendercrate.com/3D') {
            finalUrl = 'https://rendercrate.com/';
        }
        if (finalUrl === 'https://productioncrate.com/3D') {
            finalUrl = 'https://productioncrate.com/3D/';
        }
        return rewriteMenuUrlToCurrentOrigin(finalUrl, locationOrigin);
    };

    useEffect(() => {
        initCurrentPath();
    }, [initCurrentPath]);


    const renderMenuContent = (mainCat, user) => {
        let maxRows;

        if (window.innerWidth < 1024) {
            maxRows = 20;
        }else{
            maxRows = 18;
        }
        const categories = Array.isArray(mainCat.categories) ? mainCat.categories : [];
        const menuColumns = buildMenuColumns(categories, maxRows);

        //console.log(menuColumns);
        return (
            menuColumns.map((cols, k) =>
                <div  className={`content_wrapper fade-in`} key={`menu-column-${k}`}>
                    {cols.map((category, j) => 
                        <div 
                            className="menu_lol" 
                            key={`mainCategory-${mainCat.id}-${k}-categories-${j}-${category.id}`}
                        >
                            {category.link &&
                            <a className="title" href={replaceDomainsLoggedIn(category.link, user, mainCat.slug??mainCat.id, category.id,  null, category.customLink?category.link:false)}>
                                {category.name} 
                                <img src={`${baseHref}/images/icon.external_link.svg`} className="icon" />
                            </a>
                            }
                            {!category.link &&
                            <span className="title">
                                {category.name}
                            </span>
                            }
                            {category.subcategories && category.subcategories.length>0 && 
                            <div className={`submenu`}>
                                {category.subcategories.map((subcategory, h) =>
                                <a href={replaceDomainsLoggedIn(subcategory.link, user, mainCat.slug??mainCat.id, subcategory.search?'search':category.id, subcategory.id, subcategory.customLink?subcategory.link:false)} className="submenuItem" key={`mainCategory-${mainCat.id}-${k}-categories-${j}-${category.id}-submenu-${h}`}>
                                    {subcategory.name}
                                    {subcategory.tag &&
                                    <span className="tag">{subcategory.tag}</span>
                                    }
                                </a>
                                )}
                            </div>
                            }
                        </div>
                    )}
                </div>
            )
        );
    };


    return (
        <>
        <div className="big_menu" data-main-menu="1" ref={ref}
            onMouseLeave={() => {
                setMenuWidth(0);
                //console.log("MOUSE LEFT");
                initCurrentPath();
            }}
        >
            <div className="top_menu" >
                {mainCategories.map((mainCategory, i) =>
                <div 
                    key={`key-main_category-${i}=${mainCategory.id}`} 
                    className="menu_heading"
                    onMouseEnter={({currentTarget}) => {
                        const element = currentTarget.querySelector('[data-title]');
                        const parentWithPosition = element.closest('[data-main-menu]');
                        //console.log(parentWithPosition);
                        const elementRect = element.getBoundingClientRect();
                        const parentRect = parentWithPosition.getBoundingClientRect();

                        // Calculate x position relative to the grandparent
                        const positionX = elementRect.left - parentRect.left;
                        const leftPositionMenu = positionX + (element.getBoundingClientRect().width)/2;


                        setMenuLeft(leftPositionMenu);
                        setMenuWidth(element.getBoundingClientRect().width);

                        setCurrentPath('/'+mainCategory.id);
                    }}
                >
                    <a 
                        className={`title ${currentPathResolved && (!currentPath || currentPath.includes('/'+mainCategory.id))?'active':'disabled'}`}
                        data-title={mainCategory.name} 
                        data-alternate={mainCategory.alternate??mainCategory.name} 
                        href={replaceDomainsLoggedIn(mainCategory.link, user, mainCategory.id)}
                    >
                        {renderMainCategoryLabel(mainCategory)}
                    </a>
                    {mainCategory.categories?.length > 0 &&
                    <div className={`content_container ${i < 3 ? 'left-side' : ''} ${i >= mainCategories.length - 2 ? 'right-side' : ''}`}>
                        {renderMenuContent(mainCategory, user)}
                    </div>
                    }
                </div>
                )} 
            </div> 
            <div className="little_guy" style={{left: `${menuLeft}px`, width: `${menuWidth}px`}}></div>
        </div>
        { /* mobile menu */}
        <div className={`floating_menu ${visible?'visible':''}`} ref={floatingMenuRef}>
            <div className="barrier"></div>
            <span onClick={closeCallback} className={`close glyphicon glyphicon-cross`} ></span>
            <a className={`title`} href="/account">
                Account <img src={`${baseHref}/images/icon.external_link.svg`} style={{height: '40px', filter: 'invert(1)', marginLeft: '8px'}} className="icon" />
            </a>
            {user &&
            <button
                type="button"
                className="title logout"
                onClick={() => {
                    closeCallback();
                    onLogout();
                }}
            >
                <i className="glyphicon glyphicon-lock" aria-hidden="true"></i>
                Logout
            </button>
            }
            {mainCategories.map((mainCategory, i) =>
                <React.Fragment key={`mainCategory2-${mainCategory.id}-categories-${i}`}>
                    <span className={`title ${floatingMenuExpand === i?'visible':''}`} onClick={() => setFloatingMenuExpand(prev => prev ===null || prev !== i?i:null)} >
                        {renderMainCategoryLabel(mainCategory)}
                        <span className="glyphicon glyphicon-down"></span>
                    </span>
                    <div className={`list ${floatingMenuExpand === i?'visible':''}`}>
                    {(Array.isArray(mainCategory.categories) ? mainCategory.categories : []).map((category, k) =>
                        <React.Fragment key={`mainCategory3-${mainCategory.id}-categories-${i}-${k}-${category.id}`}>
                        {category.link &&
                        <a 
                            className="subtitle" 
                            href={replaceDomainsLoggedIn(category.link, user, mainCategory.slug??mainCategory.id, category.id, null, category.customLink?category.link:false)}
                        >
                            {category.name} <img src={`${baseHref}/images/icon.external_link.svg`} className="icon" />
                        </a>
                        }
                        {!category.link &&
                        <span className="subtitle" > {category.name}</span>
                        }
                        {category.subcategories?.map((subcategory, j) =>
                            <a 
                                href={replaceDomainsLoggedIn(subcategory.link, user, mainCategory.slug??mainCategory.id, subcategory.search?'search':category.id, subcategory.id, subcategory.customLink?subcategory.link:false)}
                                className="link" key={`mainCategory-${mainCategory.id}-categories-${i}-${category.id}-submenu-${j}`}>
                                - {subcategory.name}
                                {subcategory.tag &&
                                <span className="tag">{subcategory.tag}</span>
                                }
                            </a>
                        )}
                        </React.Fragment>
                    )}
                    </div>
                </React.Fragment>
            )}
        </div>
        </>
    );
};


export default forwardRef(Menu);
